import { useState } from "react";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useToast } from "@/components/base/Toast";
import { createRole, deleteRole, type PermissionModule } from "@/services/userApi";
import CycleBadge from "./CycleBadge";
import RoleFormModal from "./RoleFormModal";
import type { AppUser, Role } from "@/types";
import { plural } from "@/utils/format";

interface RolesPanelProps {
  roles: Role[];
  users: AppUser[];
  modules: PermissionModule[];
  onChanged: () => void;
}

export default function RolesPanel({ roles, users, modules, onChanged }: RolesPanelProps) {
  const { notify } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [toDelete, setToDelete] = useState<Role | null>(null);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (role: Role) => {
    setEditing(role);
    setFormOpen(true);
  };

  const duplicate = async (role: Role) => {
    try {
      await createRole({
        name: `${role.name} (copie)`.slice(0, 60),
        description: role.description,
        cycleScope: role.cycleScope,
        permissions: [...role.permissions],
      });
      onChanged();
      notify(`Rôle « ${role.name} » dupliqué.`);
    } catch (err) {
      notify(err instanceof Error ? err.message : "Duplication impossible.", "error");
    }
  };

  const requestDelete = (role: Role) => {
    if ((role.userCount ?? 0) > 0) {
      notify(
        `Impossible de supprimer « ${role.name} » : ${plural(role.userCount, "utilisateur")} y sont rattachés.`,
        "error",
      );
      return;
    }
    setToDelete(role);
  };

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm text-foreground-600">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs">
            <i className="ri-shield-keyhole-line text-primary-600"></i>
            {plural(roles.length, "rôle")} défini(s)
          </span>
          <span className="hidden text-xs text-foreground-500 sm:inline">
            Nombre de rôles illimité — cloisonnement par cycle
          </span>
        </div>
        <Button icon="ri-add-line" onClick={openCreate}>
          Créer un rôle
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {roles.map((role) => {
          const userCount = role.userCount ?? users.filter((u) => u.roleId === role.id).length;
          const permCount = role.isSystem
            ? modules.reduce((acc, m) => acc + m.permissions.length, 0)
            : modules.reduce(
                (acc, m) => acc + m.permissions.filter((p) => role.permissions.includes(p.id)).length,
                0,
              );
          return (
            <Card key={role.id} className="flex flex-col animate-slide-up">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">
                    <i className="ri-shield-user-line text-xl"></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold font-heading text-foreground-950">{role.name}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <CycleBadge scope={role.cycleScope} />
                      {role.isSystem && (
                        <Badge tone="primary" icon="ri-lock-line">
                          Système
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <p className="mt-3 flex-1 text-xs leading-relaxed text-foreground-600">
                {role.description || "Aucune description."}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Badge tone="neutral" icon="ri-group-line">
                  {plural(userCount, "utilisateur")}
                </Badge>
                <Badge tone="accent" icon="ri-key-2-line">
                  {plural(permCount, "permission")}
                </Badge>
              </div>

              <div className="mt-4 flex items-center gap-2 border-t border-background-200 pt-3">
                {role.isSystem ? (
                  <p className="text-xs text-foreground-500">
                    Accès complet à l'école, non modifiable.
                  </p>
                ) : (
                  <>
                    <Button size="sm" variant="outline" icon="ri-edit-line" onClick={() => openEdit(role)}>
                      Modifier
                    </Button>
                    <Button size="sm" variant="ghost" icon="ri-file-copy-line" onClick={() => duplicate(role)}>
                      Dupliquer
                    </Button>
                    <button
                      onClick={() => requestDelete(role)}
                      className="ml-auto press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                      aria-label="Supprimer le rôle"
                    >
                      <i className="ri-delete-bin-6-line"></i>
                    </button>
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <RoleFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        role={editing}
        modules={modules}
        onSaved={onChanged}
      />

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer le rôle"
        message={`Supprimer définitivement le rôle « ${toDelete?.name} » ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            try {
              await deleteRole(toDelete.id);
              onChanged();
              notify("Rôle supprimé.", "info");
            } catch (err) {
              notify(err instanceof Error ? err.message : "Suppression impossible.", "error");
            }
          }
        }}
      />
    </>
  );
}